import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Button, MenuItem, Modal, Select, Typography } from '@mui/material';
import { MRT_Localization_RU } from 'material-react-table/locales/ru';
import { MaterialReactTable } from 'material-react-table';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import useSnackbar from '../../hooks/useSnackbar';
import { useNavigate } from 'react-router-dom';
import {
  getAdminReferralsInfo,
  updateRewardStatusAPI,
  getAdminsInfo,
  updateBulkRewardStatusAPI,
} from '../../http/reportsAPI';
import MySnackbar from '../MySnackbar';

// Конфигурация статусов с переводами
const STATUS_CONFIG = {
  approved: { label: 'Оплачен', color: 'success' },
  pending: { label: 'В ожидании оплаты', color: 'warning' },
  declined: { label: 'Отклонён', color: 'error' },
};
const STATUS_OPTIONS = Object.keys(STATUS_CONFIG);

const TABLE_CONFIG = {
  COMMON: {
    enableStickyHeader: true,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    localization: MRT_Localization_RU,
    muiTablePaperProps: {
      sx: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    },
    muiTableContainerProps: { sx: { flex: 1 } },
  },
};

const MODAL_STYLE = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '95%',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 3,
  borderRadius: 2,
  height: '80vh',
  display: 'flex',
  flexDirection: 'column',
};

const AdminReferralsModal = React.memo(({ admin, onClose, snack, fetchAdmins }) => {
  const [users, setUsers] = useState([]);
  const [filterStatus, setFilterStatus] = useState('Все');
  const [rowSelection, setRowSelection] = useState({}); // Используем объект вместо Set

  const loadUsers = useCallback(
    async (adminId) => {
      try {
        const data = await getAdminReferralsInfo(adminId);
        return (data || []).map((reward) => ({
          id: reward.id,
          registrationDate: new Date(reward.date),
          rewardStatus: reward.status,
          name: reward.name,
          phone: reward.phone,
        }));
      } catch (error) {
        snack.handleChange(true, 'Ошибка загрузки пользователей', 'error');
        return [];
      }
    },
    [snack]
  );

  useEffect(() => {
    const loadData = async () => {
      const loadedUsers = await loadUsers(admin.id);
      setUsers(loadedUsers);
    };
    loadData();
  }, [admin.id, loadUsers]);

  useEffect(() => {
    setRowSelection({}); // Сбрасываем через пустой объект
  }, [filterStatus]);

  const handleBulkUpdate = useCallback(
    async (status) => {
      const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);
  
      try {
        await updateBulkRewardStatusAPI(selectedIds, status);
        setUsers((prev) =>
          prev.map((user) =>
            selectedIds.includes(user.id) ? { ...user, rewardStatus: status } : user
          )
        );
        setRowSelection({});
  
        // Перезапрос администраторов после массового обновления
        await fetchAdmins();
  
        snack.handleChange(true, 'Массовое обновление выполнено успешно', 'success');
      } catch (error) {
        snack.handleChange(true, 'Ошибка массового обновления', 'error');
      }
    },
    [rowSelection, snack, fetchAdmins]
  );

  const handleStatusChange = async (userId, newStatus) => {
    try {
      // Обновление статуса пользователя
      await updateRewardStatusAPI(userId, newStatus);
      
      // Обновляем локально данные пользователей
      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, rewardStatus: newStatus } : user
        )
      );
  
      // Отправляем уведомление об успешном обновлении
      snack.handleChange(true, 'Статус обновлен', 'success');
      
      // После изменения статуса выполняем перезапрос администраторов
      await fetchAdmins();
    } catch (error) {
      snack.handleChange(true, 'Ошибка обновления статуса', 'error');
    }
  };

  const userColumns = useMemo(
    () => [
      { accessorKey: 'name', header: 'Имя', size: 100 },
      { accessorKey: 'phone', header: 'Телефон', size: 80 },
      {
        accessorKey: 'rewardStatus',
        header: 'Статус',
        filterVariant: 'select',
        filterSelectOptions: [
          { text: 'Все', value: '' }, // Добавляем опцию "Все"
          ...STATUS_OPTIONS.map((option) => ({
            text: STATUS_CONFIG[option].label,
            value: option,
          })),
        ],
        Cell: ({ row }) => (
          <Select
            sx={{height:'25px', width: '100%'}}
            value={row.original.rewardStatus}
            onChange={async (e) => {
              await handleStatusChange(row.original.id, e.target.value);  // Используем handleStatusChange
            }}
            size='small'
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {STATUS_CONFIG[option].label}
              </MenuItem>
            ))}
          </Select>
        ),
        size: 100,
      },
      {
        accessorKey: 'registrationDate', // Используем прямой доступ к значению
        id: 'date',
        header: 'Дата',
        filterVariant: 'date-range',
        // Форматирование отображения даты
        Cell: ({ cell }) => {
          try {
            const dateValue = new Date(cell.getValue());
            return isNaN(dateValue)
              ? 'Некорректная дата'
              : dateValue.toLocaleDateString('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                });
          } catch {
            return 'Ошибка даты';
          }
        },
        // Фильтрация с учетом временных зон
        filterFn: (row, columnId, filterValue) => {
          try {
            const rowDate = new Date(row.getValue(columnId));
            if (isNaN(rowDate)) return false;

            const [start, end] = filterValue || [];
            if (!start && !end) return true;

            const timezoneOffset = rowDate.getTimezoneOffset() * 60000;
            const localDate = new Date(rowDate.getTime() - timezoneOffset);

            if (start) {
              const filterStart = new Date(start);
              filterStart.setHours(0, 0, 0, 0);
              if (localDate < filterStart) return false;
            }

            if (end) {
              const filterEnd = new Date(end);
              filterEnd.setHours(23, 59, 59, 999);
              if (localDate > filterEnd) return false;
            }

            return true;
          } catch {
            return false;
          }
        },
        size: 120,
        enableSorting: true,
      },
    ],
    [snack]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Modal open={!!admin} onClose={onClose}>
        <Box sx={MODAL_STYLE}>
        <MySnackbar {...snack} />
          <Typography variant='h6' sx={{ mb: 2 }}>
            Рефералы администратора: {admin?.name}
          </Typography>

          <MaterialReactTable
            {...TABLE_CONFIG.COMMON}
            initialState={{ showColumnFilters: true }}
            columns={userColumns}
            data={users}
            enableRowSelection
            getRowId={(row) => {
              return row.id;
            }}
            onRowSelectionChange={setRowSelection} // Прямая передача состояния
            state={{ rowSelection }}
          />

          <Box sx={{ mt: 2, gap: 2, display: 'flex' }}>
            <Button
              variant='contained'
              onClick={() => handleBulkUpdate('approved')}
              disabled={!Object.values(rowSelection).some(Boolean)}
            >
              Оплатить ({Object.values(rowSelection).filter(Boolean).length})
            </Button>
            <Button
              variant='outlined'
              color='error'
              onClick={() => handleBulkUpdate('declined')}
              disabled={!Object.values(rowSelection).some(Boolean)}
            >
              Отклонить ({Object.values(rowSelection).filter(Boolean).length})
            </Button>
          </Box>
        </Box>
      </Modal>
    </LocalizationProvider>
  );
});

const AdminsReferralInfo = () => {
  const navigate = useNavigate();
  const snack = useSnackbar();
  const [admins, setAdmins] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  const fetchAdmins = useCallback(async () => {
    try {
      const data = await getAdminsInfo();
      if (!data) return;
  
      const processedAdmins = data.map(({ id, name, internalName, phone, reffCount, reffPendingCount }) => ({
        id,
        name: internalName || name,
        phone,
        reffCount,
        reffPendingCount,
      }));
  
      setAdmins(processedAdmins);
    } catch (error) {
      snack.handleChange(true, 'Ошибка загрузки администраторов', 'error');
    }
  }, [snack]);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const adminColumns = useMemo(
    () => [
      { accessorKey: 'name', header: 'Имя', size: 150 },
      { accessorKey: 'phone', header: 'Телефон', size: 170 },
      { accessorKey: 'reffCount', header: 'Всего приглашённых', size: 130 },
      { accessorKey: 'reffPendingCount', header: 'В ожидании оплаты', size: 130 },
      {
        accessorKey: 'actions',
        header: 'Действия',
        Cell: ({ row }) => (
          <Button sx={{fontSize:'12px'}} variant='outlined' onClick={() => setSelectedAdmin(row.original)}>
            Показать рефералов
          </Button>
        ),
        enableSorting: false,
        size: 150,
      },
    ],
    []
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='ru'>
      <Box sx={{ p: 3, width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant='h5'>Реферальная система</Typography>
          <Button variant='contained' onClick={() => navigate('/settings')}>
            Настройки
          </Button>
        </Box>

        <MaterialReactTable {...TABLE_CONFIG.COMMON} columns={adminColumns} data={admins} />
        
        {selectedAdmin && (
          <AdminReferralsModal 
            admin={selectedAdmin} 
            onClose={() => setSelectedAdmin(null)} 
            snack={snack}
            fetchAdmins={fetchAdmins}
          />
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default AdminsReferralInfo;
